import { countries as Countries } from 'utils/constants'

export type CountryOption = {
  code: string
  label: string
}

/** The cleared state of the country filter. `code: ''` means "no filter", which
 *  is what makes the unfiltered whole-queue view reachable from the UI. */
export const ALL_COUNTRIES_OPTION: CountryOption = {
  code: '',
  label: 'All countries',
}

/**
 * Human label for an ISO alpha-2 code, e.g. "Germany — DE".
 *
 * Uses the common short name, not `{ select: 'official' }`: the official form
 * ("United Kingdom of Great Britain and Northern Ireland") overflowed the
 * filter field and made every long-named country unreadable.
 */
export const countryLabel = (code: string): string => {
  const upper = String(code ?? '')
    .trim()
    .toUpperCase()
  return `${Countries.getName(upper, 'en') || upper} — ${upper}`
}

/**
 * Build the country filter's options from the codes that actually have
 * escalations.
 *
 * Normalising to upper case happens BEFORE de-duplicating, which is the whole
 * fix: the previous implementation merged raw zone country codes into a Set
 * that already held the upper-case Nordic codes. Those zone entries are stored
 * verbatim in db_auth (`zone_add` inserts them unchanged, and the column is a
 * plain 2–255 char string), so it holds a mix of 'dk', 'DK' and even full names
 * like 'Germany'. The Set kept every variant, and the dropdown showed the same
 * country two or three times with identical labels.
 */
export const buildCountryOptions = (codes: string[]): CountryOption[] => {
  const seen = new Set<string>()
  const options: CountryOption[] = []

  codes.forEach((raw) => {
    const code = String(raw ?? '')
      .trim()
      .toUpperCase()
    if (!/^[A-Z]{2}$/.test(code) || seen.has(code)) return
    seen.add(code)
    options.push({ code, label: countryLabel(code) })
  })

  options.sort((a, b) => a.label.localeCompare(b.label))
  // "All countries" is pinned first rather than sorted in with the rest.
  return [ALL_COUNTRIES_OPTION, ...options]
}

/**
 * RisikaAPI serialises naive UTC timestamps. Without the trailing Z the browser
 * reads them as local time and the displayed instant is wrong by the offset.
 */
export const formatEscalationDate = (value?: string | null): string => {
  if (!value) return '—'
  const iso = value.endsWith('Z') ? value : `${value}Z`
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
